import * as yup from "yup";

import { FormStateType } from "../Helpers/formTypes";
import { NorwegianId } from "norwegian-national-id-validator";
import { BookingErrorsDto } from "@apiModels/BookingSteps/BookingErrorsDto.csharp";
import { WaitlistViewModel } from "../../../Blocks/Waitlist/WaitlistViewModel.csharp";
import {
  isUnderage,
  maxAgeNotBlocking,
  minAgeNotBlocking,
} from "../Helpers/personalInfoFormStepHelpers";
import { ThirdStepBookingLabelsDto } from "@apiModels/BookingSteps/ThirdStepBookingLabelsDto.csharp";
import { AppointmentBookingSystem } from "@apiModels/AppointmentBookingSystem.csharp";

export const thirdStepValidator = (
  formState: FormStateType,
  errors: BookingErrorsDto,
  waitlist: WaitlistViewModel | undefined,
  requireParentPersonalNumber: boolean,
  thirdStepBookingLabels: ThirdStepBookingLabelsDto,
  requirePreparationRequirements: boolean,
  blockedSsnList: string[],
  blockedSsnErrorMessage: string,
  hasRemoteOptions: boolean,
) =>
  yup.object().shape({
    remoteContact: yup
      .string()
      .nullable()
      .test("required", errors.fieldIsRequired, (value) => {
        if (hasRemoteOptions) {
          return !!value;
        }
        return true;
      }),
    mobileNumber: yup
      .string()
      .test("required", errors.fieldIsRequired, (value) => !!value)
      .test(
        "type",
        errors.telephone,
        (value) => value != undefined && !isNaN(Number(value)),
      )
      .test(
        "length",
        errors.telephone,
        (value) => value != undefined && value.length >= 8,
      ),
    personalIdNumber: yup
      .string()
      .test("required", errors.fieldIsRequired, (value) => !!value)
      .test(
        "type",
        errors.personalNumber,
        (value) => value != undefined && !isNaN(Number(value)),
      )
      .test(
        "length",
        errors.personalNumber,
        (value) => value != undefined && value.toString().length == 11,
      )
      .test(
        "valid",
        errors.personalNumber,
        (value) => value != undefined && NorwegianId(value).isValid(),
      )
      .test(
        "minAge",
        formState.appointment.minMaxAge?.minAgeError || "",
        (value) => minAgeNotBlocking(value, formState.appointment.minMaxAge),
      )
      .test(
        "maxAge",
        formState.appointment.minMaxAge?.maxAgeError || "",
        (value) => maxAgeNotBlocking(value, formState.appointment.minMaxAge),
      )
      .test(
        "blockedSsn",
        blockedSsnErrorMessage,
        (value) => !blockedSsnList.includes(value ?? ""),
      ),
    parentPersonalIdNumber: yup
      .string()
      .test(
        "required",
        errors.fieldIsRequired,
        (value) => !requireParentPersonalNumber || !!value,
      )
      .test(
        "valid",
        errors.personalNumber,
        (value) =>
          !requireParentPersonalNumber ||
          (!!value && NorwegianId(value).isValid()),
      )
      .test("minAge", errors.parentPersonalNumber, (value) => {
        if (requireParentPersonalNumber) {
          return !!value && isUnderage(value, 18) === false;
        }
        return true;
      })
      .test(
        "blockedSsn",
        blockedSsnErrorMessage,
        (value) => !blockedSsnList.includes(value ?? ""),
      ),
    name: yup
      .string()
      .test(
        "required",
        errors.fieldIsRequired,
        (value) =>
          formState.appointment.appointmentBookingSystem ===
            AppointmentBookingSystem.Metodika ||
          waitlist?.isMetodika === true ||
          !!value,
      ),
    firstName: yup
      .string()
      .test(
        "required",
        errors.fieldIsRequired,
        (value) =>
          (formState.appointment.appointmentBookingSystem ===
            AppointmentBookingSystem.Dips &&
            !waitlist?.isMetodika) ||
          !!value,
      ),
    lastName: yup
      .string()
      .test(
        "required",
        errors.fieldIsRequired,
        (value) =>
          (formState.appointment.appointmentBookingSystem ===
            AppointmentBookingSystem.Dips &&
            !waitlist?.isMetodika) ||
          !!value,
      ),
    notes: yup.string().test("required", errors.fieldIsRequired, (value) => {
      if (formState.appointment.requireNotes) return !!value;
      return true;
    }),
    emailAddress: yup
      .string()
      .test("required", errors.fieldIsRequired, (value) => !!value)
      .email(errors.email),
    subscribeToNewsletter: yup.boolean(),
    terms: yup.boolean().test("required", errors.terms, (value) => {
      if (!!waitlist) {
        return true;
      }
      return !!value;
    }),
    consent: yup
      .boolean()
      .test("required", errors.policy, (value) => {
        if (!thirdStepBookingLabels.dropdownLabels.consent) {
          return true;
        }
        return !!value;
      })
      .oneOf([true], errors.policy),
    preparationRequirements: yup
      .boolean()
      .test("required", errors.preparationRequirements, (value) => {
        if (
          !formState.appointment.preparationRequirementsDto ||
          !requirePreparationRequirements
        ) {
          return true;
        }

        return !!value;
      }),
    doctorPreparationRequirements: yup
      .boolean()
      .test("required", errors.preparationRequirements, (value) => {
        if (!formState.appointment.doctorPreparationRequirementsDto) {
          return true;
        }

        return !!value;
      }),
  });

export const thirdStepConfirmValidator = (errors: BookingErrorsDto) =>
  yup.object().shape({
    smsCode: yup
      .string()
      .nullable()
      .test("required", errors.smsCode, (value) => !!value)
      .test("length", errors.smsCode, (value) => !!value && value.length == 6),
  });
